/**
 * Full-resolution export, sidecars and batch delivery.
 *
 * The interactive image is a 2560px proxy, so an export cannot simply save what
 * is on screen. Every export re-decodes the original file at full resolution
 * and runs it through the same `LumosRenderer` the viewport uses — same
 * programs, same order — then downsamples on the CPU and encodes.
 *
 * The whole file is written around one constraint: a 45 MP frame is roughly
 * 180 MB as RGBA bytes, and several times that inside the GPU. Everything here
 * releases its buffers at the first moment it can, and batches run strictly one
 * photo at a time.
 */

import {
  type CaptureFacts,
  type DecodedImage,
  type LocalAdjustments,
  type MaskLayer,
  type Recipe,
  RECIPE_VERSION,
  SCALAR_OPERATIONS,
  clampToSpec,
  defaultMask,
  defaultRecipe,
  readPath,
  sanitiseCurve,
  writePath,
} from '@lumos/core';
import { LumosRenderer } from '../engine/renderer.ts';
import { decodeFile } from '../raw/decoderClient.ts';
import type { Photo } from '../state/store.ts';

export type ExportFormat = 'jpeg' | 'png' | 'webp';

export interface ExportOptions {
  format: ExportFormat;
  /**
   * 1..100, the scale an export dialog is expected to speak in. Converted to
   * the 0..1 the canvas encoder takes. Ignored for PNG, which is lossless.
   */
  quality: number;
  /** Longest edge of the delivered image; `null` keeps the sensor's own size. */
  longEdge: number | null;
  /** Appended to the source filename, before the new extension. */
  filenameSuffix: string;
}

export type ExportStage = 'decoding' | 'rendering' | 'resizing' | 'encoding' | 'done';

export interface ExportProgress {
  stage: ExportStage;
  message: string;
}

export type ExportProgressFn = (progress: ExportProgress) => void;

export interface BatchProgress extends ExportProgress {
  /** 0-based index of the photo currently running. */
  index: number;
  total: number;
  photoId: string;
  filename: string;
}

export interface ExportResult {
  photoId: string;
  /** The source file. */
  filename: string;
  /** The name the exported image was given. */
  outputFilename: string;
  status: 'ok' | 'failed' | 'cancelled';
  /** Only present on the result handed to `onPhoto`; see `exportMany`. */
  blob?: Blob;
  bytes?: number;
  width?: number;
  height?: number;
  error?: string;
}

export interface BatchHooks {
  /**
   * Called as each photo settles, with the blob attached. Save it here — the
   * returned result list deliberately drops blobs so a long batch does not sit
   * in memory until the dialog closes.
   */
  onPhoto?: (result: ExportResult) => void;
  /** Checked between photos. A render in flight cannot be interrupted. */
  shouldCancel?: () => boolean;
}

export const SIDECAR_VERSION = 1 as const;
export const SIDECAR_SUFFIX = '.lumos.json';
export const APP_VERSION = '0.1.0';

export interface Sidecar {
  version: typeof SIDECAR_VERSION;
  filename: string;
  exportedAt: string;
  recipe: Recipe;
  capture: CaptureFacts | null;
  appVersion: string;
}

export const DEFAULT_EXPORT_OPTIONS: ExportOptions = {
  format: 'jpeg',
  quality: 92,
  longEdge: null,
  // A default suffix keeps an exported JPEG from landing on top of a source
  // JPEG with the same name in the same folder.
  filenameSuffix: '-lumos',
};

const MIME: Record<ExportFormat, string> = {
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};

const EXTENSION: Record<ExportFormat, string> = {
  jpeg: 'jpg',
  png: 'png',
  webp: 'webp',
};

export const FORMAT_LABEL: Record<ExportFormat, string> = {
  jpeg: 'JPEG',
  png: 'PNG',
  webp: 'WebP',
};

interface Frame {
  data: Uint8ClampedArray<ArrayBuffer>;
  width: number;
  height: number;
}

export interface OutputSize {
  width: number;
  height: number;
  /** True when the long-edge setting actually reduced the frame. */
  resized: boolean;
}

// ---------------------------------------------------------------- filenames --

function baseName(filename: string): string {
  const separator = Math.max(filename.lastIndexOf('/'), filename.lastIndexOf('\\'));
  const name = separator === -1 ? filename : filename.slice(separator + 1);
  const dot = name.lastIndexOf('.');
  return (dot > 0 ? name.slice(0, dot) : name).trim() || 'photo';
}

/** Keeps a typed suffix from producing a path, a hidden file or a broken name. */
export function sanitiseSuffix(suffix: string): string {
  return suffix.replace(/[^\w .-]+/g, '').replace(/^\.+/, '').slice(0, 40).trim();
}

export function exportFilename(photo: Photo, options: ExportOptions): string {
  return `${baseName(photo.filename)}${sanitiseSuffix(options.filenameSuffix)}.${EXTENSION[options.format]}`;
}

export function sidecarFilename(photo: Photo, options: ExportOptions): string {
  return `${baseName(photo.filename)}${sanitiseSuffix(options.filenameSuffix)}${SIDECAR_SUFFIX}`;
}

// ----------------------------------------------------------------- geometry --

function fitToLongEdge(
  width: number,
  height: number,
  longEdge: number | null,
): OutputSize {
  const natural = Math.max(width, height);
  // Never upscale. Enlarging an export invents detail the sensor never recorded,
  // and a client file that is bigger than the capture is a lie about the capture.
  if (longEdge === null || !Number.isFinite(longEdge) || longEdge >= natural) {
    return { width, height, resized: false };
  }
  const scale = Math.max(1, Math.round(longEdge)) / natural;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
    resized: true,
  };
}

/**
 * The pixel dimensions this export will produce, for the dialog's live readout.
 * Returns `null` when the photo's pixels are not resident, because the full
 * size is only known once the file has been decoded at least once.
 */
export function exportDimensions(photo: Photo, options: ExportOptions): OutputSize | null {
  const image = photo.image;
  if (!image) return null;

  // Mirrors LumosRenderer.processedSize, but against the full-resolution
  // dimensions rather than the working proxy.
  const swapped = photo.recipe.orientation % 2 === 1;
  const baseWidth = swapped ? image.fullHeight : image.fullWidth;
  const baseHeight = swapped ? image.fullWidth : image.fullHeight;
  const crop = photo.recipe.crop;
  const width = Math.max(1, Math.round(baseWidth * (crop?.width ?? 1)));
  const height = Math.max(1, Math.round(baseHeight * (crop?.height ?? 1)));

  return fitToLongEdge(width, height, options.longEdge);
}

// ------------------------------------------------------------------ resampling --

/** sRGB decode table. 256 entries, so the inner loop is a lookup, not a `pow`. */
const SRGB_TO_LINEAR = (() => {
  const table = new Float32Array(256);
  for (let i = 0; i < 256; i += 1) {
    const value = i / 255;
    table[i] = value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  }
  return table;
})();

function linearToSrgbByte(value: number): number {
  const clamped = value <= 0 ? 0 : value >= 1 ? 1 : value;
  const encoded =
    clamped <= 0.0031308 ? clamped * 12.92 : 1.055 * clamped ** (1 / 2.4) - 0.055;
  return Math.round(encoded * 255);
}

interface AxisWeights {
  starts: Int32Array;
  counts: Int32Array;
  weights: Float32Array;
  taps: number;
}

/**
 * Per-destination coverage of the source axis. Each destination sample owns the
 * half-open source interval `[i·scale, (i+1)·scale)`, and every source pixel it
 * touches contributes in proportion to how much of it falls inside.
 */
function axisWeights(sourceLength: number, targetLength: number): AxisWeights {
  const scale = sourceLength / targetLength;
  const taps = Math.max(1, Math.ceil(scale) + 1);
  const starts = new Int32Array(targetLength);
  const counts = new Int32Array(targetLength);
  const weights = new Float32Array(targetLength * taps);

  for (let i = 0; i < targetLength; i += 1) {
    const from = i * scale;
    const to = (i + 1) * scale;
    const first = Math.min(sourceLength - 1, Math.floor(from));
    const last = Math.min(sourceLength - 1, Math.max(first, Math.ceil(to) - 1));

    let total = 0;
    let count = 0;
    for (let j = first; j <= last && count < taps; j += 1) {
      const weight = Math.max(0, Math.min(j + 1, to) - Math.max(j, from));
      weights[i * taps + count] = weight;
      total += weight;
      count += 1;
    }
    if (total <= 0) {
      weights[i * taps] = 1;
      total = 1;
      count = 1;
    }
    for (let k = 0; k < count; k += 1) weights[i * taps + k] /= total;

    starts[i] = first;
    counts[i] = count;
  }

  return { starts, counts, weights, taps };
}

/**
 * Area-average ("box") downscale, written out here rather than handed to
 * `drawImage`.
 *
 * Canvas smoothing — even at `imageSmoothingQuality: 'high'` — is specified
 * loosely and in practice reads a small fixed neighbourhood per destination
 * pixel. Taking an 8000px edge down to 2048 that way ignores roughly fifteen of
 * every sixteen source pixels, so hair, fabric weave and foliage alias into
 * moiré exactly where a client looks first. Averaging every contributing pixel
 * costs one pass over the frame and removes the failure entirely.
 *
 * The averaging happens in linear light: the render chain wrote display-encoded
 * sRGB, and averaging encoded values darkens fine bright detail against dark
 * surroundings (specular highlights in hair, catchlights, backlit rim).
 */
function boxResize(frame: Frame, targetWidth: number, targetHeight: number): Frame {
  const columns = axisWeights(frame.width, targetWidth);
  const rows = axisWeights(frame.height, targetHeight);

  // Horizontal first, into linear-light RGB. Alpha is not carried: an exported
  // photograph is opaque, and dropping it saves a quarter of the work.
  const horizontal = new Float32Array(targetWidth * frame.height * 3);
  for (let y = 0; y < frame.height; y += 1) {
    const sourceRow = y * frame.width * 4;
    const targetRow = y * targetWidth * 3;
    for (let x = 0; x < targetWidth; x += 1) {
      const base = x * columns.taps;
      const start = columns.starts[x];
      const count = columns.counts[x];
      let r = 0;
      let g = 0;
      let b = 0;
      for (let k = 0; k < count; k += 1) {
        const weight = columns.weights[base + k];
        const pixel = sourceRow + (start + k) * 4;
        r += SRGB_TO_LINEAR[frame.data[pixel]] * weight;
        g += SRGB_TO_LINEAR[frame.data[pixel + 1]] * weight;
        b += SRGB_TO_LINEAR[frame.data[pixel + 2]] * weight;
      }
      const out = targetRow + x * 3;
      horizontal[out] = r;
      horizontal[out + 1] = g;
      horizontal[out + 2] = b;
    }
  }

  const data = new Uint8ClampedArray(targetWidth * targetHeight * 4);
  for (let y = 0; y < targetHeight; y += 1) {
    const base = y * rows.taps;
    const start = rows.starts[y];
    const count = rows.counts[y];
    const targetRow = y * targetWidth * 4;
    for (let x = 0; x < targetWidth; x += 1) {
      let r = 0;
      let g = 0;
      let b = 0;
      for (let k = 0; k < count; k += 1) {
        const weight = rows.weights[base + k];
        const pixel = ((start + k) * targetWidth + x) * 3;
        r += horizontal[pixel] * weight;
        g += horizontal[pixel + 1] * weight;
        b += horizontal[pixel + 2] * weight;
      }
      const out = targetRow + x * 4;
      data[out] = linearToSrgbByte(r);
      data[out + 1] = linearToSrgbByte(g);
      data[out + 2] = linearToSrgbByte(b);
      data[out + 3] = 255;
    }
  }

  return { data, width: targetWidth, height: targetHeight };
}

// -------------------------------------------------------------------- export --

/** Lets the browser paint the stage message before the next long synchronous block. */
function nextTask(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

async function encode(frame: Frame, options: ExportOptions): Promise<Blob> {
  const canvas = new OffscreenCanvas(frame.width, frame.height);
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) throw new Error('Could not open a 2D canvas to encode the export.');

  // `ImageData` will not accept an array that might be backed by a
  // SharedArrayBuffer. This is a view over the same bytes with the narrower
  // type — re-copying a 45 MP frame here would double the peak memory.
  const pixels = new Uint8ClampedArray(
    frame.data.buffer as ArrayBuffer,
    frame.data.byteOffset,
    frame.data.length,
  );
  context.putImageData(new ImageData(pixels, frame.width, frame.height), 0, 0);
  return canvas.convertToBlob({
    type: MIME[options.format],
    quality:
      options.format === 'png'
        ? undefined
        : Math.min(100, Math.max(1, Math.round(options.quality))) / 100,
  });
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Renders one photo at full resolution and returns the encoded file.
 *
 * Throws with a readable message on any failure; `exportMany` turns those into
 * per-photo results instead of stopping the batch.
 */
export async function exportPhoto(
  photo: Photo,
  options: ExportOptions,
  onProgress?: ExportProgressFn,
): Promise<Blob> {
  const report = (stage: ExportStage, message: string) => onProgress?.({ stage, message });

  let image: DecodedImage | null = null;
  let renderer: LumosRenderer | null = null;

  try {
    report('decoding', `Reading ${photo.filename} at full resolution`);
    image = await decodeFile(photo.file, photo.filename, {
      maxWorkingEdge: Number.POSITIVE_INFINITY,
      full: true,
      onProgress: (_stage, message) => report('decoding', message),
    }).promise;

    report('rendering', 'Applying the recipe');
    await nextTask();

    // A 1x1 backing canvas: the chain renders into framebuffers and is read
    // back from there, so the canvas itself is never drawn to.
    renderer = new LumosRenderer(new OffscreenCanvas(1, 1));
    renderer.setImage(image);
    renderer.process(photo.recipe, image, {
      showClipping: false,
      overlayMask: -1,
      overlayStrength: 0,
      splitPosition: null,
    });

    const frame = renderer.readProcessed();
    if (!frame) throw new Error('The renderer produced no frame to encode.');

    // The GPU targets are the largest allocation in the application. Release
    // them the moment the pixels are on the CPU, not at the end of the export,
    // or a batch accumulates a full pipeline per photo until the tab dies.
    renderer.dispose();
    renderer = null;
    image = null;

    const target = fitToLongEdge(frame.width, frame.height, options.longEdge);
    let output = frame;
    if (target.resized) {
      report('resizing', `Resizing to ${target.width} × ${target.height}`);
      await nextTask();
      output = boxResize(frame, target.width, target.height);
    }

    report('encoding', `Encoding ${FORMAT_LABEL[options.format]}`);
    await nextTask();
    const blob = await encode(output, options);
    report('done', `${output.width} × ${output.height}, ${formatBytes(blob.size)}`);
    return blob;
  } finally {
    renderer?.dispose();
    renderer = null;
    image = null;
  }
}

/**
 * Exports a list of photos one at a time.
 *
 * Sequential is not a simplification: two full-resolution renders at once will
 * exhaust GPU memory on the machines this runs on. A failure is recorded and
 * the batch continues, so one unreadable file does not cost the other forty.
 */
export async function exportMany(
  photos: readonly Photo[],
  options: ExportOptions,
  onProgress?: (progress: BatchProgress) => void,
  hooks: BatchHooks = {},
): Promise<ExportResult[]> {
  const results: ExportResult[] = [];

  for (let index = 0; index < photos.length; index += 1) {
    const photo = photos[index];
    const outputFilename = exportFilename(photo, options);
    const identity = { photoId: photo.id, filename: photo.filename, outputFilename };

    if (hooks.shouldCancel?.()) {
      const cancelled: ExportResult = { ...identity, status: 'cancelled' };
      results.push(cancelled);
      hooks.onPhoto?.(cancelled);
      continue;
    }

    try {
      const size = exportDimensions(photo, options);
      const blob = await exportPhoto(photo, options, (progress) =>
        onProgress?.({ ...progress, index, total: photos.length, ...identity }),
      );
      const settled: ExportResult = {
        ...identity,
        status: 'ok',
        bytes: blob.size,
        width: size?.width,
        height: size?.height,
      };
      results.push(settled);
      hooks.onPhoto?.({ ...settled, blob });
    } catch (error) {
      const failed: ExportResult = { ...identity, status: 'failed', error: messageOf(error) };
      results.push(failed);
      hooks.onPhoto?.(failed);
    }

    // Between photos, never during: the previous export's buffers are only
    // collectable once this turn ends, and the UI needs a frame to update.
    await nextTask();
  }

  return results;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Hands a blob to the browser's download machinery. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Revoking immediately truncates large downloads in some browsers, which have
  // not finished reading the blob when the click handler returns.
  setTimeout(() => URL.revokeObjectURL(url), 20_000);
}

// ------------------------------------------------------------------ sidecars --

export function exportSidecar(photo: Photo): Blob {
  const sidecar: Sidecar = {
    version: SIDECAR_VERSION,
    filename: photo.filename,
    exportedAt: new Date().toISOString(),
    recipe: structuredClone(photo.recipe),
    capture: photo.image?.capture ?? null,
    appVersion: APP_VERSION,
  };
  return new Blob([JSON.stringify(sidecar, null, 2)], { type: 'application/json' });
}

/**
 * Reads a sidecar back into a recipe, or returns `null`.
 *
 * A sidecar is a plain text file a user can edit, so it is treated with the
 * same suspicion as a model's output: every value is bounded by the same
 * operation registry the planner passes through, and anything unrecognised is
 * dropped rather than guessed at.
 */
export function readSidecar(text: string): Recipe | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }

  const source = asRecord(parsed);
  if (!source) return null;
  if (source.version !== SIDECAR_VERSION) return null;

  const recipe = asRecord(source.recipe);
  if (!recipe) return null;
  if (recipe.version !== RECIPE_VERSION) return null;

  return coerceRecipe(recipe);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function numberOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/**
 * Bounds for local adjustments. The core validator keeps its own copy private,
 * and core is a fixed contract, so this mirrors it rather than reaching in.
 */
const LOCAL_BOUNDS: Record<keyof LocalAdjustments, [number, number]> = {
  exposure: [-5, 5],
  contrast: [-100, 100],
  highlights: [-100, 100],
  shadows: [-100, 100],
  whites: [-100, 100],
  blacks: [-100, 100],
  temperature: [-100, 100],
  tint: [-100, 100],
  saturation: [-100, 100],
  vibrance: [-100, 100],
  clarity: [-100, 100],
  texture: [-100, 100],
  sharpness: [-100, 100],
  noise: [0, 100],
};

function coerceRecipe(source: Record<string, unknown>): Recipe {
  const recipe = defaultRecipe();
  const raw = source as unknown as Recipe;

  // Every scalar the registry knows about is bounded by its own spec.
  for (const spec of SCALAR_OPERATIONS) {
    const value = readPath(raw, spec.path);
    if (typeof value === 'number' && Number.isFinite(value)) {
      writePath(recipe, spec.path, clampToSpec(spec, value));
    }
  }

  const curves = asRecord(source.curves);
  if (curves) {
    for (const channel of ['rgb', 'red', 'green', 'blue'] as const) {
      const points = sanitiseCurve(curves[channel]);
      if (points) recipe.curves[channel] = points;
    }
  }

  if (typeof source.orientation === 'number' && Number.isFinite(source.orientation)) {
    recipe.orientation = ((Math.round(source.orientation) % 4) + 4) % 4;
  }
  recipe.flipHorizontal = source.flipHorizontal === true;
  recipe.flipVertical = source.flipVertical === true;

  const crop = asRecord(source.crop);
  if (crop) {
    const x = clamp(numberOr(crop.x, 0), 0, 0.99);
    const y = clamp(numberOr(crop.y, 0), 0, 0.99);
    const width = clamp(numberOr(crop.width, 1), 0.01, 1 - x);
    const height = clamp(numberOr(crop.height, 1), 0.01, 1 - y);
    recipe.crop = { x, y, width, height };
  }

  if (Array.isArray(source.masks)) {
    recipe.masks = source.masks
      .slice(0, 16)
      .map((mask, index) => coerceMask(mask, index))
      .filter((mask): mask is MaskLayer => mask !== null);
  }

  return recipe;
}

function coerceMask(raw: unknown, index: number): MaskLayer | null {
  const source = asRecord(raw);
  if (!source) return null;

  const kind = source.kind;
  if (kind !== 'radial' && kind !== 'linear' && kind !== 'luminance' && kind !== 'color') {
    return null;
  }

  const mask = defaultMask(
    kind,
    typeof source.id === 'string' && source.id ? source.id.slice(0, 64) : `sidecar-${index}`,
    typeof source.name === 'string' && source.name ? source.name.slice(0, 60) : `Layer ${index + 1}`,
  );

  mask.enabled = source.enabled !== false;
  mask.invert = source.invert === true;
  mask.feather = clamp(numberOr(source.feather, mask.feather), 0, 100);
  mask.opacity = clamp(numberOr(source.opacity, mask.opacity), 0, 100);
  mask.origin = source.origin === 'ai' ? 'ai' : 'manual';
  if (typeof source.rationale === 'string') mask.rationale = source.rationale.slice(0, 400);

  const radial = asRecord(source.radial);
  if (radial) {
    mask.radial = {
      cx: clamp(numberOr(radial.cx, 0.5), -0.5, 1.5),
      cy: clamp(numberOr(radial.cy, 0.5), -0.5, 1.5),
      rx: clamp(numberOr(radial.rx, 0.32), 0.02, 2),
      ry: clamp(numberOr(radial.ry, 0.32), 0.02, 2),
      angle: clamp(numberOr(radial.angle, 0), -180, 180),
    };
  }

  const linear = asRecord(source.linear);
  if (linear) {
    mask.linear = {
      x1: clamp(numberOr(linear.x1, 0.5), -1, 2),
      y1: clamp(numberOr(linear.y1, 0.15), -1, 2),
      x2: clamp(numberOr(linear.x2, 0.5), -1, 2),
      y2: clamp(numberOr(linear.y2, 0.6), -1, 2),
    };
  }

  const luminance = asRecord(source.luminance);
  if (luminance) {
    const low = clamp(numberOr(luminance.low, 0), 0, 1);
    const high = clamp(numberOr(luminance.high, 0.5), 0, 1);
    mask.luminance = {
      low: Math.min(low, high),
      high: Math.max(low, high),
      lowFeather: clamp(numberOr(luminance.lowFeather, 0.1), 0, 0.5),
      highFeather: clamp(numberOr(luminance.highFeather, 0.15), 0, 0.5),
    };
  }

  const color = asRecord(source.color);
  if (color) {
    mask.color = {
      hue: ((numberOr(color.hue, 25) % 360) + 360) % 360,
      hueRange: clamp(numberOr(color.hueRange, 30), 5, 180),
      minSaturation: clamp(numberOr(color.minSaturation, 0.08), 0, 1),
    };
  }

  const adjustments = asRecord(source.adjustments);
  if (adjustments) {
    for (const key of Object.keys(LOCAL_BOUNDS) as (keyof LocalAdjustments)[]) {
      const value = adjustments[key];
      if (typeof value !== 'number' || !Number.isFinite(value)) continue;
      const [low, high] = LOCAL_BOUNDS[key];
      mask.adjustments[key] = clamp(value, low, high);
    }
  }

  return mask;
}
